
import { FC, useState } from 'react';
import { useSupabase } from '../../hooks/useSupabase';

const ReservationSection: FC = () => {
  const { supabase } = useSupabase();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    date: '',
    time: '',
    guests: 2,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!supabase) {
      alert('시스템 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      // 예약 데이터 저장
      const { data: reservation, error } = await supabase
        .from('reservations')
        .insert([{
          name: formData.name,
          phone: formData.phone,
          date: formData.date,
          time: formData.time,
          guests: formData.guests,
          message: formData.message,
          status: 'pending'
        }])
        .select()
        .single();

      if (error) throw error;

      // 카카오톡 알림 발송
      try {
        const { data: functionData, error: functionError } = await supabase.functions.invoke('send-kakao-notification', {
          body: { reservation }
        });

        if (functionError) {
          console.error('카카오톡 알림 발송 실패:', functionError);
        } else {
          console.log('카카오톡 알림 발송 성공:', functionData);
        }
      } catch (notificationError) {
        console.error('카카오톡 알림 발송 중 오류:', notificationError);
        // 알림 발송 실패해도 예약은 성공으로 처리
      }

      // 폼 초기화 및 성공 메시지 표시
      setFormData({
        name: '',
        phone: '',
        date: '',
        time: '',
        guests: 2,
        message: ''
      });
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);

    } catch (error) {
      console.error('예약 저장 실패:', error);
      alert('예약 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeSlots = [
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
    '20:00', '20:30', '21:00', '21:30', '22:00'
  ];

  return (
    <section id="reservation" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            예약하기
          </h2>
          <p className="text-xl text-gray-600">
            편리한 온라인 예약으로 특별한 식사를 준비하세요
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Reservation Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">
                예약 정보
              </h3>
              
              <form id="reservation-form" onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      성함 *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      placeholder="성함을 입력하세요"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      연락처 *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      placeholder="연락처를 입력하세요"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                      예약 날짜 *
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-2">
                      예약 시간 *
                    </label>
                    <div className="relative">
                      <select
                        id="time"
                        name="time"
                        value={formData.time}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm appearance-none"
                      >
                        <option value="">시간 선택</option>
                        {timeSlots.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="guests" className="block text-sm font-medium text-gray-700 mb-2">
                    인원 수 *
                  </label>
                  <div className="relative">
                    <select
                      id="guests"
                      name="guests"
                      value={String(formData.guests)}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm appearance-none"
                    >
                      {[...Array(20)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}명</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    요청사항
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="특별한 요청사항이 있으시면 입력해주세요 (최대 500자)"
                  />
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.message.length}/500
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-red-600 to-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      예약 중...
                    </>
                  ) : (
                    <>
                      <i className="ri-calendar-check-line mr-2"></i>
                      예약하기
                    </>
                  )}
                </button>

                {showSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    <i className="ri-check-circle-line mr-2"></i>
                    예약이 성공적으로 접수되었습니다. 확인 후 연락드리겠습니다.
                  </div>
                )}

              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">
                  예약 안내
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <i className="ri-information-line text-blue-600 mt-1 mr-3"></i>
                    <div>
                      <p className="font-medium text-gray-800">예약 확인</p>
                      <p className="text-gray-600 text-sm">예약 접수 후 확인 전화를 드립니다</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-time-line text-orange-600 mt-1 mr-3"></i>
                    <div>
                      <p className="font-medium text-gray-800">영업시간</p>
                      <p className="text-gray-600 text-sm">평일 11:00~23:00 (라스트오더 22:30)</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-phone-line text-green-600 mt-1 mr-3"></i>
                    <div>
                      <p className="font-medium text-gray-800">전화 예약</p>
                      <p className="text-gray-600 text-sm">02-883-8150</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-red-600 to-blue-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">
                  찾아오시는 길
                </h3>
                <div className="space-y-3 text-white/90">
                  <div className="flex items-start">
                    <i className="ri-map-pin-line mr-3 mt-1"></i>
                    <div>
                      <p className="font-medium">주소</p>
                      <p className="text-sm">서울 관악구 봉천동 1625-17</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-subway-line mr-3 mt-1"></i>
                    <div>
                      <p className="font-medium">지하철</p>
                      <p className="text-sm">서울대입구역 1번출구 800M<br />낙성대역 4번출구 500M</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-building-line mr-3 mt-1"></i>
                    <div>
                      <p className="font-medium">주변 랜드마크</p>
                      <p className="text-sm">인헌초등학교 정문 맞은편</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReservationSection;
